import type {
  ControlBridgeStructureGroup,
  ControlBridgeStructureNode,
  KryspaceControlBridgeStructure,
} from '../../types/kryspaceControlBridgeStructure';
import { scopeIdsEqual } from '../../utils/scopeIdPlane';
import { inScope, type ScopeContext } from './filters';
import { deriveProvisionalRoleLens, type RoleLens } from './derivation/roleLens';

export function nodeScopeKey(node: ControlBridgeStructureNode): string {
  return node.filterScope ?? node.key;
}

/** True when ``scope`` is this node's filterScope, key, or artifactInstanceId (UUID-normalized). */
export function nodeMatchesScope(scope: string, node: ControlBridgeStructureNode): boolean {
  if (!scope) return false;
  return (
    scopeIdsEqual(nodeScopeKey(node), scope) ||
    scopeIdsEqual(node.key, scope) ||
    scopeIdsEqual(node.artifactInstanceId, scope)
  );
}

export function scopeMatchesNode(scope: string, node: ControlBridgeStructureNode): boolean {
  if (nodeMatchesScope(scope, node)) return true;
  return (node.children ?? []).some((child) => scopeMatchesNode(scope, child));
}

export function scopeInGroup(group: ControlBridgeStructureGroup, scope: string): boolean {
  if (scope === 'company') return false;
  return group.nodes.some((node) => scopeMatchesNode(scope, node));
}

export function findGroupIdForScope(
  structure: KryspaceControlBridgeStructure,
  scope: string
): string | null {
  if (scope === 'company') return null;
  for (const group of structure.groups) {
    if (scopeInGroup(group, scope)) return group.id;
  }
  return null;
}

export function findDomainKeyForScope(
  structure: KryspaceControlBridgeStructure,
  scope: string
): string | null {
  if (scope === 'company') return null;
  for (const group of structure.groups) {
    for (const node of group.nodes) {
      if (scopeMatchesNode(scope, node)) return node.key;
    }
  }
  return null;
}

export interface ScopeBreadcrumbCrumb {
  key: string;
  label: string;
}

function walkNodePath(
  node: ControlBridgeStructureNode,
  scope: string,
  trail: ScopeBreadcrumbCrumb[]
): ScopeBreadcrumbCrumb[] | null {
  const here = { key: nodeScopeKey(node), label: node.label };
  const nextTrail = [...trail, here];
  if (nodeMatchesScope(scope, node)) return nextTrail;
  for (const child of node.children ?? []) {
    const hit = walkNodePath(child, scope, nextTrail);
    if (hit) return hit;
  }
  return null;
}

/** FE-DRV-5: company → domain → subdomain path for the active rail scope. */
export function scopeBreadcrumb(
  scope: string,
  structure: KryspaceControlBridgeStructure,
  options?: { fallbackLabel?: string }
): ScopeBreadcrumbCrumb[] {
  const company: ScopeBreadcrumbCrumb = {
    key: 'company',
    label: structure.companyTitle?.trim() || 'Company',
  };
  if (!scope || scope === 'company') {
    return [company];
  }
  for (const group of structure.groups) {
    for (const node of group.nodes) {
      const hit = walkNodePath(node, scope, [company]);
      if (hit) return hit;
    }
  }
  const label = options?.fallbackLabel?.trim() || scope;
  return [company, { key: scope, label }];
}

export interface DriveWatchStripNode {
  key: string;
  label: string;
  inFlow?: number;
  blocked?: number;
  revenueActual?: number;
  costActual?: number;
}

function collectDomainChildNodes(structure: KryspaceControlBridgeStructure): ControlBridgeStructureNode[] {
  const out: ControlBridgeStructureNode[] = [];
  for (const group of structure.groups) {
    if (group.id !== 'domains') continue;
    for (const domain of group.nodes) {
      out.push(...(domain.children ?? []));
    }
  }
  return out;
}

function lensForDomainChildren(
  children: ControlBridgeStructureNode[],
  options?: { roleId?: string; lens?: RoleLens | null }
): RoleLens {
  const mapped = children.map((n) => ({ id: nodeScopeKey(n), label: n.label }));
  const incoming = options?.lens;
  if (incoming?.domains?.length) {
    const overlapping = incoming.domains.filter((d) =>
      mapped.some((c) => scopeIdsEqual(c.id, d.domainRef))
    );
    if (overlapping.length) {
      return { roleId: incoming.roleId, source: incoming.source, domains: overlapping };
    }
  }
  return deriveProvisionalRoleLens(options?.roleId ?? 'gm', mapped);
}

/**
 * FE-DRV-7 / FE-ARCH-2: GM-watch **subdomain children of every domain**, not top-level
 * domains. Visible at company and while drilled into a primary leaf. The current
 * scope (and descendants) are omitted from the strip list.
 *
 * Incoming ``lens`` is used only when it already names at least one of those
 * children (manifest / subdomain grain). Top-level GM Business/Customer rows
 * do not overlap, so we derive a child GM lens instead of tagging Business ``out``.
 */
export function watchChildNodesForScope(
  structure: KryspaceControlBridgeStructure,
  scope: string,
  options?: { roleId?: string; lens?: RoleLens | null }
): DriveWatchStripNode[] {
  const children = collectDomainChildNodes(structure);
  if (!children.length) return [];
  const lens = lensForDomainChildren(children, options);
  const watch: DriveWatchStripNode[] = [];
  for (const child of children) {
    const key = nodeScopeKey(child);
    const hit = lens.domains.find((d) => scopeIdsEqual(d.domainRef, key));
    if (hit?.tier !== 'watch') continue;
    if (scope && scope !== 'company' && scopeMatchesNode(scope, child)) continue;
    watch.push({ key, label: child.label });
  }
  return watch;
}

/** Drive artifact in-flow / blocked for a watch strip. Null when there is no artifact plane. */
export function countWatchOccupancy(
  scopeKey: string,
  artifacts: Array<{ filterScope?: string; subdomainId?: string; status?: string }>,
  scopeContext?: ScopeContext
): { inFlow: number; blocked: number } | null {
  if (!artifacts.length) return null;
  let inFlow = 0;
  let blocked = 0;
  for (const artifact of artifacts) {
    const domain = artifact.filterScope || artifact.subdomainId;
    if (!domain) continue;
    if (!inScope(domain, scopeKey, scopeContext) && !scopeIdsEqual(domain, scopeKey)) continue;
    if (artifact.status === 'blocked') blocked += 1;
    else inFlow += 1;
  }
  return { inFlow, blocked };
}

export function occupancyFromRollupNodes(
  scopeKey: string,
  nodes: Array<{
    subdomain_id?: string;
    subdomainId?: string;
    revenue_actual?: unknown;
    revenueActual?: unknown;
    cost_actual?: unknown;
    costActual?: unknown;
  }>
): { revenueActual?: number; costActual?: number } | null {
  if (!nodes.length) return null;
  let revenue: number | undefined;
  let cost: number | undefined;
  let hit = false;
  for (const node of nodes) {
    const id = String(node.subdomainId || node.subdomain_id || '');
    if (!id) continue;
    if (
      !scopeIdsEqual(id, scopeKey) &&
      !id.includes(scopeKey) &&
      !scopeKey.includes(id)
    ) {
      continue;
    }
    hit = true;
    const rev = node.revenueActual ?? node.revenue_actual;
    const cst = node.costActual ?? node.cost_actual;
    if (typeof rev === 'number' && Number.isFinite(rev)) {
      revenue = (revenue ?? 0) + rev;
    }
    if (typeof cst === 'number' && Number.isFinite(cst)) {
      cost = (cost ?? 0) + cst;
    }
  }
  if (!hit) return null;
  const out: { revenueActual?: number; costActual?: number } = {};
  if (revenue != null) out.revenueActual = revenue;
  if (cost != null) out.costActual = cost;
  return Object.keys(out).length ? out : null;
}

/** Accordion state for sidebar groups/domains — not the Company Control Bridge rail. */
export function initialSidebarCollapseState(
  structure: KryspaceControlBridgeStructure,
  options?: { focusSurface?: boolean }
): {
  groups: Record<string, boolean>;
  domains: Record<string, boolean>;
} {
  const collapseAll = options?.focusSurface === true;
  const groups: Record<string, boolean> = {};
  const domains: Record<string, boolean> = {};
  for (const group of structure.groups) {
    groups[group.id] = collapseAll;
    for (const node of group.nodes) {
      if (node.children?.length) {
        domains[node.key] = collapseAll;
      }
    }
  }
  return { groups, domains };
}

export function expandSidebarForScope(
  structure: KryspaceControlBridgeStructure,
  scope: string,
  collapsedGroups: Record<string, boolean>,
  collapsedDomains: Record<string, boolean>
): { groups: Record<string, boolean>; domains: Record<string, boolean> } {
  const groups = { ...collapsedGroups };
  const domains = { ...collapsedDomains };
  const groupId = findGroupIdForScope(structure, scope);
  if (groupId) {
    groups[groupId] = false;
  }
  const domainKey = findDomainKeyForScope(structure, scope);
  if (domainKey) {
    domains[domainKey] = false;
  }
  return { groups, domains };
}

export function toggleCollapsedMap(
  map: Record<string, boolean>,
  key: string
): Record<string, boolean> {
  return { ...map, [key]: !map[key] };
}

/** Persists Company Control Bridge structure sidebar rail vs open tree. */
export const CONTROL_BRIDGE_SIDEBAR_COLLAPSED_KEY = 'krysalis.controlBridge.sidebarCollapsed';

/**
 * Read the operator's last sidebar rail preference.
 * Missing key or any value other than `'1'` → open (`false`).
 * Only a stored `'1'` starts collapsed. First visit does not write.
 */
export function readSidebarCollapsedPreference(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(CONTROL_BRIDGE_SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeSidebarCollapsedPreference(collapsed: boolean): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(CONTROL_BRIDGE_SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* ignore quota / private-mode */
  }
}
